import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BACKEND_URL } from '../config/canonical';
import {
  StickyNote, User, Search, RefreshCw, ExternalLink, Eye, EyeOff, Shield
} from 'lucide-react';

const CaseNotes = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCases(await res.json());
    } catch (e) {
      console.error('Fetch cases error:', e);
    }
  }, [token]);

  const fetchNotes = useCallback(async () => {
    if (!selectedCase) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setNotes(await res.json());
    } catch (e) {
      console.error('Fetch notes error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCase]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const visibilityIcon = (v) => {
    if (v === 'ADMIN_ONLY') return <Shield className="w-3 h-3 text-red-400" />;
    if (v === 'CLIENT_VIEWABLE') return <Eye className="w-3 h-3 text-green-400" />;
    return <EyeOff className="w-3 h-3 text-slate-400" />;
  };

  const filtered = notes.filter(n => {
    if (!search) return true;
    const s = search.toLowerCase();
    return n.content?.toLowerCase().includes(s) || n.subject?.toLowerCase().includes(s) || n.author_name?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="case-notes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Case Notes</h1>
          <p className="text-slate-400">Internal notes and observations</p>
        </div>
        {selectedCase && (
          <Link to={`/cases/${selectedCase}`}>
            <Button variant="outline" className="border-slate-300 text-slate-300">
              <ExternalLink className="w-4 h-4 mr-2" /> View Case Detail
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={selectedCase} onValueChange={setSelectedCase}>
              <SelectTrigger className="bg-slate-50 border-slate-300 text-slate-900" data-testid="notes-case-select">
                <SelectValue placeholder="Select a case..." />
              </SelectTrigger>
              <SelectContent>
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-50 border-slate-300 text-slate-900 pl-10" placeholder="Search notes..." />
            </div>
            <Button variant="outline" className="border-slate-300 text-slate-300" onClick={fetchNotes}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {!selectedCase ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <StickyNote className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Select a Case</h3>
            <p className="text-slate-400">Choose a case from the dropdown to view its notes</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="text-center py-12 text-slate-400">Loading notes...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <StickyNote className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Notes Found</h3>
            <p className="text-slate-400">{search ? 'Try a different search' : 'No notes for this case yet'}</p>
            <Link to={`/cases/${selectedCase}`}>
              <Button className="mt-4 bg-[#0F172A] hover:bg-[#1E293B] text-white">Add Note from Case Detail</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(note => (
            <Card key={note.id} className="bg-white border-slate-200" data-testid={`note-card-${note.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-slate-300 text-slate-300 text-xs">{note.category}</Badge>
                    {note.subject && <span className="text-white font-medium text-sm">{note.subject}</span>}
                  </div>
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    {visibilityIcon(note.visibility)} {note.visibility?.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-slate-200 text-sm whitespace-pre-wrap">{note.content}</p>
                {note.follow_up_required && (
                  <Badge className="bg-yellow-500/20 text-yellow-400 text-xs mt-2">
                    Follow-up Required {note.follow_up_date ? `by ${note.follow_up_date}` : ''}
                  </Badge>
                )}
                <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                  <User className="w-3 h-3" /> {note.author_name} &middot; {new Date(note.created_at).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CaseNotes;
